import { useEventInfo } from '@shared/utils/eventInfo';
import { useSearchAccommodationsQueryMetadataPayload } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { useMemo } from 'react';
import { addError } from '@shared/utils/logger';
import { addAction } from '@shared/utils/logger';
import {
  AccommodationCatalogUnitsOccupancyInput,
  TravelCurrencyCode,
  TravelPlacePricingAndBookingDetailsPayload,
  useAccommodationPlacesPricingAndBookingDetailsQuery,
} from '@graphql/generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type Parameters = Readonly<{
  dates: {
    checkIn: string;
    checkOut: string;
  };
  occupancies: AccommodationCatalogUnitsOccupancyInput;
}>;

/**
 * Hook for fetching accommodation place pricing and booking details without relying on cache.
 *
 * The previously existing pricing hook is tightly coupled with cache, which can lead to
 * stale or inconsistent pricing data, especially in the Accommodation console where up-to-date
 * pricing is critical. This hook disables caching by using the 'no-cache' fetch policy, ensuring
 * that every query returns the latest pricing and booking details from the server.
 *
 * @param travelPlaceIds - Array of accommodation place IDs to fetch pricing for.
 * @param overrides - Optional overrides for check-in/check-out dates and occupancies.
 * @returns An object containing pricing data, loading state, error, and the effective dates.
 */

export const useAccommodationPlacesPricing = (travelPlaceIds: string[], parameters: Parameters) => {
  const { eventInfo, loading: isLoadingEventInfo } = useEventInfo();

  const currencyCode = useTravelWhiteLabelCurrency();
  const [localeCurrencyOnOrOff, loadingUseLocaleCurrencyFlag] = useIsFeatureEnabled('localeBasedHotelCurrency');

  const { isPreparingSearchMetadataPayload, searchMetadata } = useSearchAccommodationsQueryMetadataPayload();

  const payload: TravelPlacePricingAndBookingDetailsPayload | undefined = useMemo(() => {
    if (loadingUseLocaleCurrencyFlag || isPreparingSearchMetadataPayload || !eventInfo?.eventId) {
      return undefined;
    }

    return {
      currencyCode: (localeCurrencyOnOrOff ? currencyCode : null) ?? TravelCurrencyCode.USD,
      dates: {
        checkIn: {
          // validated with skip
          high: parameters.dates?.checkIn,
          low: parameters.dates?.checkIn,
        },
        // validated with skip
        checkOut: {
          high: parameters.dates?.checkOut,
          low: parameters.dates?.checkOut,
        },
      },
      occupancies: parameters.occupancies,
      metadata: searchMetadata,
      accommodationPlaceIds: travelPlaceIds,
    };
  }, [
    loadingUseLocaleCurrencyFlag,
    isPreparingSearchMetadataPayload,
    eventInfo?.eventId,
    localeCurrencyOnOrOff,
    currencyCode,
    parameters.dates?.checkIn,
    parameters.dates?.checkOut,
    parameters.occupancies,
    searchMetadata,
    travelPlaceIds,
  ]);

  const {
    data,
    loading: travelPlacesPricingAndBookingDetailsLoading,
    error,
  } = useAccommodationPlacesPricingAndBookingDetailsQuery({
    fetchPolicy: 'no-cache',
    batchMode: 'fast',
    variables: {
      payload: payload!,
    },
    skip: !payload || !travelPlaceIds.length,
    onError(error) {
      addError(error, {
        source: 'Pricing api failed',
        context: 'travelPlacesPricingAndBookingDetails',
        errorMessage: error.message,
        checkInDate: parameters.dates?.checkIn,
        checkOutDate: parameters.dates?.checkOut,
        currencyCode: payload?.currencyCode,
        metadata: payload?.metadata,
        accommodationPlaceIds: payload?.accommodationPlaceIds,
        joyPlaceIds: payload?.joyPlaceIds,
      });
    },
    onCompleted(data) {
      const accommodationPlaces = data?.travelPlacesPricingAndBookingDetails?.accommodationPlaces;
      if (!accommodationPlaces?.length) {
        addAction('Pricing api empty results', {
          context: 'travelPlacesPricingAndBookingDetails',
          checkInDate: parameters.dates?.checkIn,
          checkOutDate: parameters.dates?.checkOut,
          currencyCode: payload?.currencyCode,
          metadata: payload?.metadata,
          accommodationPlaceIds: payload?.accommodationPlaceIds,
          joyPlaceIds: payload?.joyPlaceIds,
        });
      }
    },
  });

  const isLoading = travelPlacesPricingAndBookingDetailsLoading || loadingUseLocaleCurrencyFlag || isPreparingSearchMetadataPayload || isLoadingEventInfo;

  return {
    data: data,
    loading: isLoading,
    error: error,
  };
};
